import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import sampleImage from '../assets/sample-image.svg';
import { characterMap } from '../assets/characters';
import { SEOMeta } from '../templates/SEOMeta';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import CategorySelector from './CategorySelector';
import { logger } from '../utils/logger';
import { useLocalizedNavigate, useLocalizedPath } from '../i18n/hooks';
import { 
  getOrCreateSessionToken, 
  getExistingSessionToken, 
  touchSession,
  clearSession,
  hasValidSession
} from '../utils/sessionManager';

// helper para traducir identificador de personaje a asset
const characterAsset = (id?: string) => {
  if (!id) return null;
  return characterMap[id] || null;
};

  // Agrupar mensajes por playerId manteniendo orden cronológico (más reciente último)
  const groupMessagesByPlayer = (msgs: Message[]) => {
    const map = new Map<number, Message[]>();
    msgs.forEach(m => {
      const arr = map.get(m.playerId) || [];
      arr.push(m);
      map.set(m.playerId, arr);
    });
    return Array.from(map.entries()).map(([playerId, messages]) => ({ playerId, messages }));
  };

interface Player {
  id: number;
  name: string;
  role?: 'PLAYER' | 'IMPOSTOR';
  status: 'ALIVE' | 'ELIMINATED' | 'DISCONNECTED';
  isConnected: boolean;
  connectionStatus: 'CONNECTING' | 'READY' | 'DISCONNECTED';
}

interface Message {
  id: number;
  playerName: string;
  playerId: number;
  content: string;
  isVisible: boolean;
}

interface VoteResult {
  playerId: number;
  playerName: string;
  voteCount: number;
  eliminated: boolean;
}

interface GameState {
  status: 'WAITING' | 'ASSIGNING_ROLES' | 'ROUND_WRITING' | 'ROUND_READING' | 'VOTING' | 'RESULTS' | 'FINISHED';
  currentRound: number;
  character?: string;
  isImpostor?: boolean;
  roundDurationSeconds: number;
  votingDurationSeconds: number;
  categoryId?: number;
  categoryName?: string;
  // turn-based additions
  turnOrder?: number[];
  currentTurnIndex?: number;
  currentPlayerId?: number;
  currentPlayerName?: string;
}

const GameRoom: React.FC = () => {
  const { t } = useTranslation();
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useLocalizedNavigate();
  const localizePath = useLocalizedPath();
  const [players, setPlayers] = useState<Player[]>([]);
  const [gameState, setGameState] = useState<GameState>({
    status: 'WAITING',
    currentRound: 0,
    roundDurationSeconds: 15,
    votingDurationSeconds: 20
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedVote, setSelectedVote] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(0);
  const [voteResults, setVoteResults] = useState<VoteResult[]>([]);
  const [processingVoteResults, setProcessingVoteResults] = useState(false);
  const [processingNextPhase, setProcessingNextPhase] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [starting, setStarting] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [roomInfo, setRoomInfo] = useState<any>(null);
  const [winningTeam, setWinningTeam] = useState<string | null>(null);
  const [winningMessage, setWinningMessage] = useState<string | null>(null);
  const [gameWord, setGameWord] = useState<string | null>(null);
  const [impostors, setImpostors] = useState<Array<{id: number, name: string}>>([]);
  const [eliminationMessage, setEliminationMessage] = useState<string | null>(null);
  const [emptyWriters, setEmptyWriters] = useState<number[]>([]);
  const [turnConfirmed, setTurnConfirmed] = useState(false);
  // Role visibility state
  const [roleVisible, setRoleVisible] = useState(false);
  const [hasRoleAssigned, setHasRoleAssigned] = useState(false);
  // Reaction phase state
  const [reactionPhase, setReactionPhase] = useState<{active: boolean; endsAt: number; authorName?: string; content?: string;}>({active: false, endsAt: 0});
  const [reactions, setReactions] = useState<Array<{ id: string; emoji: string; x: number }>>([]);
  const lastReactionTsRef = useRef<number>(0);
  const reactionButtonsRef = useRef<HTMLDivElement | null>(null);
  
  // Message animation state
  const [animatingMessage, setAnimatingMessage] = useState<{
    playerName: string;
    content: string;
    visible: boolean;
  } | null>(null);
  
  const clientRef = useRef<Client | null>(null);
  const votingSectionRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Guards to prevent auto-confirm retry loops and duplicate requests
  const autoConfirmKeyRef = useRef<string>('');
  const turnTimerStartedRef = useRef<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const playerName = localStorage.getItem('playerName');

  // Constantes para reintentos
  const MAX_RECONNECT_ATTEMPTS = 3;
  const RECONNECT_DELAYS = [1000, 3000, 5000]; // Delays progresivos en ms

  // Estados para UI personalizada (reemplazar alert y confirm)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ title: '', message: '', onConfirm: () => {} });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  // Intentar reconexión automática al montar el componente
  useEffect(() => {
    // Limpiar timeout previo si existe
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const attemptReconnect = async (attemptNumber: number = 0): Promise<void> => {
      if (!roomCode || !playerName) {
        navigate('/');
        return;
      }

      // Verificar si existe un token de sesión anterior y es válido
      const existingToken = getExistingSessionToken(roomCode);
      
      if (existingToken && hasValidSession(roomCode)) {
        logger.log(`🔄 Reconnection attempt ${attemptNumber + 1}/${MAX_RECONNECT_ATTEMPTS}`);
        setReconnecting(true);
        setReconnectError(null);
        setReconnectAttempts(attemptNumber);
        
        try {
          const response = await apiClient.post(`/api/rooms/${roomCode}/reconnect`, {
            sessionToken: existingToken
          });
          
          const data = response.data;
          logger.log('✅ Reconnection successful:', data);
          
          logger.log('🔄 Reconexión - Datos recibidos:', {
            categoryId: data.categoryId,
            categoryName: data.categoryName,
            character: data.character,
            isImpostor: data.isImpostor
          });
          
          // Restaurar el sessionId
          setSessionId(existingToken);
          touchSession(roomCode);
          
          // Restaurar el estado del juego desde los datos de reconexión
          setGameState(prev => {
            const newState = {
              ...prev,
              status: data.status || 'WAITING',
              currentRound: data.currentRound || 0,
              character: data.character,
              isImpostor: data.isImpostor,
              roundDurationSeconds: prev.roundDurationSeconds,
              votingDurationSeconds: prev.votingDurationSeconds,
              categoryId: data.categoryId,
              categoryName: data.categoryName,
              turnOrder: data.turnOrder,
              currentTurnIndex: data.currentTurnIndex,
              currentPlayerId: data.currentPlayerId,
              currentPlayerName: data.currentPlayerName
            };
            
            logger.log('🔄 Reconexión - Estado actualizado:', {
              categoryId: newState.categoryId,
              categoryName: newState.categoryName,
              character: newState.character
            });
            
            return newState;
          });
          
          // Si se recibió información del rol (character o isImpostor), marcar como asignado
          if (data.character || data.isImpostor !== undefined) {
            setHasRoleAssigned(true);
            logger.log('Role information restored:', { character: data.character, isImpostor: data.isImpostor });
          }
          
          // Restaurar tiempo restante del turno si está disponible
          if (data.turnTimeRemaining !== undefined) {
            setTurnTimeRemaining(data.turnTimeRemaining);
          }
          
          // Restaurar lista de jugadores
          if (data.players) {
            setPlayers(normalizePlayerData(data.players));
          }
          
          // Restaurar mensajes
          if (data.messages) {
            setMessages(data.messages);
          }
          
          setReconnecting(false);
          setReconnectAttempts(0);
          logger.log('✅ Reconnection completed successfully');
          
        } catch (err: any) {
          logger.error(`❌ Reconnection attempt ${attemptNumber + 1} failed:`, err);
          
          // Determinar si debemos reintentar
          const shouldRetry = attemptNumber < MAX_RECONNECT_ATTEMPTS - 1;
          const isRecoverableError = err.response?.status !== 404 && err.response?.status !== 401 && err.response?.status !== 400;
          
          if (shouldRetry && isRecoverableError) {
            // Programar reintento solo si es un error recuperable (ej: red, timeout)
            const delay = RECONNECT_DELAYS[attemptNumber] || 5000;
            logger.log(`⏱️ Retrying reconnection in ${delay}ms...`);
            setReconnectError(t('gameRoom.reconnecting.retrying', { current: attemptNumber + 1, max: MAX_RECONNECT_ATTEMPTS }));
            
            reconnectTimeoutRef.current = setTimeout(() => {
              attemptReconnect(attemptNumber + 1);
            }, delay);
          } else {
            // Falló todos los reintentos o error no recuperable (sesión expirada/inválida)
            logger.warn('❌ Reconnection failed, attempting rejoin with updated session...');
            
            setReconnectError(t('gameRoom.reconnecting.updatingSession'));
            
            
            // 🔥 CRÍTICO: Intentar rejoin sin borrar sesión antigua
            // El backend actualizará el sessionId del jugador existente
            try {
              // Esperar un momento para que el backend procese cualquier estado previo
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Obtener el token actual o crear nuevo si es necesario
              const currentToken = getExistingSessionToken(roomCode);
              const tokenToUse = currentToken || getOrCreateSessionToken(roomCode, playerName);
              const characterId = localStorage.getItem('playerCharacter') || 'char1';
              
              logger.log('🔄 Attempting rejoin with token:', tokenToUse);
              logger.log('🔄 Using playerName:', playerName);
              logger.log('🔄 Character:', characterId);
              
              const joinResponse = await apiClient.post(`/api/rooms/${roomCode}/join`, {
                playerName,
                sessionId: tokenToUse,
                character: characterId
              });
              
              logger.log('✅ Rejoin successful:', joinResponse.data);
              
              setSessionId(tokenToUse);
              setReconnecting(false);
              setReconnectError(null);
              setReconnectAttempts(0);
              
              // Cargar información de la sala
              await Promise.all([loadRoomInfo(), loadGameInfo()]);
              
            } catch (joinErr: any) {
              logger.error('❌ Rejoin failed:', joinErr);
              
              // Si el rejoin también falla, mostrar error al usuario PERO NO SALIR
              setReconnecting(false);
              setReconnectAttempts(0);
              
              const errorMsg = joinErr.response?.data?.message || joinErr.response?.data?.error || t('gameRoom.errors.reconnectFailed');
              
              // Mostrar error persistente en UI
              setError(t('gameRoom.errors.connectionError', { message: errorMsg }));
              setReconnectError(null);
              
              // NO redirigir automáticamente - dejar al usuario en la sala
              // El usuario puede recargar manualmente si lo necesita
            }
          }
        }
      } else {
        // No hay sesión previa válida, unirse con nueva sesión
        logger.log('No existing valid session, joining with new token...');
        
        try {
          const newToken = getOrCreateSessionToken(roomCode, playerName);
          const characterId = localStorage.getItem('playerCharacter') || 'char1';
          
          logger.log('🔄 Joining room with new token:', newToken);
          
          const joinResponse = await apiClient.post(`/api/rooms/${roomCode}/join`, {
            playerName,
            sessionId: newToken,
            character: characterId
          });
          
          logger.log('✅ Join successful:', joinResponse.data);
          
          setSessionId(newToken);
          setReconnecting(false);
          setReconnectAttempts(0);
          
        } catch (joinErr: any) {
          logger.error('❌ Initial join failed:', joinErr);
          
          setReconnecting(false);
          const errorMsg = joinErr.response?.data?.message || joinErr.response?.data?.error || t('gameRoom.errors.joinFailed');
          setError(errorMsg);
          
          // Redirigir al inicio después de 3 segundos
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      }
    };
    
    attemptReconnect(0);
    
    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []); // Solo ejecutar al montar el componente


  // WebSocket connection
  useEffect(() => {
    if (!sessionId || !playerName || !roomCode) {
      // Esperar a que se complete la reconexión o creación de sesión
      return;
    }

    const socket = new SockJS('/api/ws');
    const client = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = async () => {
      setConnected(true);
      
      // PRIMERO: Suscribirse a eventos de la sala ANTES de hacer cualquier otra cosa
      client.subscribe(`/topic/room/${roomCode}`, (message) => {
        const data = JSON.parse(message.body);
        handleWebSocketMessage(data);
      });
      
      logger.log('WebSocket connected and subscribed to room:', roomCode);
      
      // SEGUNDO: Marcar jugador como listo una vez establecida la conexión y suscripción
      try {
        logger.log('Marking player as ready with sessionId:', sessionId);
        if (!sessionId) {
          logger.error('SessionId is null when trying to mark player as ready');
          return;
        }
        
        const response = await apiClient.post(`/api/rooms/${roomCode}/players/ready`, {
          sessionId: sessionId
        });
        
        logger.log('Player marked as ready, response:', response.data);
        
        // Recargar la información de la sala para reflejar el cambio
        await loadRoomInfo();
        logger.log('Room info reloaded after marking ready');
      } catch (err) {
        logger.error('Error marking player as ready:', err);
        // Intentar cargar info de la sala de todas maneras
        try {
          await loadRoomInfo();
        } catch (e) {
          // Silent fail
        }
      }

      // ✅ ELIMINADO: Ya no necesitamos polling, el WebSocket nos mantiene sincronizados
      // fetchTurnState();
    };

    client.onDisconnect = () => {
      setConnected(false);
    };

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, sessionId, playerName]);

  // Mantener sesión activa con heartbeat periódico
  useEffect(() => {
    if (!roomCode || !sessionId) return;
    
    // Touch inicial
    touchSession(roomCode);
    
    // Configurar intervalo para mantener la sesión activa
    const heartbeatInterval = setInterval(() => {
      const success = touchSession(roomCode);
      if (success) {
        logger.log('💓 Session heartbeat - session kept alive');
      } else {
        logger.warn('⚠️ Session heartbeat failed - session may have expired');
      }
    }, 3 * 60 * 1000); // Cada 3 minutos (mucho antes de los 30 min de threshold)
    
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [roomCode, sessionId]);

  // Cargar información inicial de la sala
  useEffect(() => {
  loadRoomInfo();
  loadGameInfo();
  loadRoomMeta();
    // load categories
    (async () => {
      try {
        const resp = await apiClient.get('/api/categories');
        const fetchedCategories = resp.data || [];
        setCategories(fetchedCategories);
        // Solo establecer la primera categoría si no hay ninguna seleccionada (considerando actualizaciones previas)
        if (fetchedCategories.length > 0) {
          setSelectedCategoryId(prev => (prev === null ? fetchedCategories[0].id : prev));
        }
      } catch (e) {
        // Silent fail
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode]);

  const loadRoomMeta = async () => {
    try {
      const resp = await apiClient.get(`/api/rooms/${roomCode}`);
      setRoomInfo(resp.data);
    } catch (e) {
      // Silent fail
    }
  };

  // Suscribirse a mensajes personalizados una vez que tengamos el ID del jugador
  useEffect(() => {
    if (connected && clientRef.current && players.length > 0) {
      const currentPlayerId = getCurrentPlayerId();
      if (currentPlayerId > 0) {
        logger.log('🔔 Subscribing to personal messages for player ID:', currentPlayerId);
        const subscription = clientRef.current.subscribe(`/topic/room/${roomCode}/player/${currentPlayerId}`, (message) => {
          const data = JSON.parse(message.body);
          logger.log('📨 Personal message received:', data.type);
          handlePersonalMessage(data);
        });
        
        // IMPORTANTE: Si el juego ya está en ASSIGNING_ROLES o posterior, 
        // y aún no tenemos rol, solicitar inmediatamente
        if (gameState.status !== 'WAITING' && !hasRoleAssigned) {
          logger.log('⚠️ Late subscription detected - fetching role immediately');
          setTimeout(() => loadMyRole(), 100);
        }
        
        return () => {
          subscription.unsubscribe();
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, players, roomCode]);

  // Timer para countdown
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      // Cuando el contador llega a 0, activar estado de procesamiento según la fase
      if (gameState.status === 'VOTING') {
        setProcessingVoteResults(true);
      } else if (gameState.status === 'ROUND_READING' || gameState.status === 'RESULTS') {
        setProcessingNextPhase(true);
      }
    }
  }, [timeRemaining, gameState.status]);

  // separate timer for per-turn countdown
  useEffect(() => {
    if (turnTimeRemaining > 0) {
      turnTimerStartedRef.current = true;
      const t = setTimeout(() => setTurnTimeRemaining(prev => prev - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [turnTimeRemaining]);

  // Actualizar sesión periódicamente para mantenerla activa
  // Backend tiene GRACE_PERIOD de 5 minutos, actualizamos cada 4 minutos para estar seguros
  // Reducido de 2 a 4 minutos para minimizar tráfico sobre túnel Cloudflare en producción
  useEffect(() => {
    if (!roomCode || !sessionId) return;
    
    // Actualizar inmediatamente al montar
    touchSession(roomCode);
    
    const interval = setInterval(() => {
      touchSession(roomCode);
      logger.log('Session refreshed (heartbeat)');
    }, 4 * 60 * 1000); // 4 minutos - reducido para optimizar latencia en producción
    
    return () => clearInterval(interval);
  }, [roomCode, sessionId]);

  // Auto-confirm when turn time runs out for current player
  // Uses autoConfirmKeyRef to ensure only ONE auto-confirm per turn, preventing retry loops
  // when errors reset turnConfirmed and re-trigger this effect.
  useEffect(() => {
    const myId = getCurrentPlayerId();
    if (
      gameState.status === 'ROUND_WRITING' &&
      turnTimeRemaining === 0 &&
      turnTimerStartedRef.current && // Only fire if timer actually counted down (not initial state)
      gameState.currentPlayerId === myId
    ) {
      const key = `${gameState.currentRound}-${gameState.currentTurnIndex}`;
      if (!turnConfirmed && autoConfirmKeyRef.current !== key) {
        autoConfirmKeyRef.current = key;
        confirmTurn(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnTimeRemaining, gameState.status, gameState.currentPlayerId, turnConfirmed]);

  // Autoscroll cuando comience la votación
  useEffect(() => {
    if (gameState.status === 'VOTING' && votingSectionRef.current) {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        // Intentar múltiples métodos de scroll para mejor compatibilidad
        if (votingSectionRef.current) {
          // Método 1: scrollIntoView con smooth
          votingSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          // Método 2: Scroll adicional para dispositivos móviles
          setTimeout(() => {
            if (votingSectionRef.current) {
              const element = votingSectionRef.current;
              const elementTop = element.offsetTop;
              const elementHeight = element.offsetHeight;
              const windowHeight = window.innerHeight;
              
              // Calcular posición para centrar mejor en móviles
              const scrollPosition = elementTop - (windowHeight / 2) + (elementHeight / 2);
              
              window.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: 'smooth'
              });
            }
          }, 300);
        }
      }, 150);
    }
  }, [gameState.status]);

  // Autoscroll al textarea cuando es el turno del jugador actual
  useEffect(() => {
    if (
      gameState.status === 'ROUND_WRITING' &&
      gameState.currentPlayerId === getCurrentPlayerId() &&
      textareaRef.current
    ) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          // Focus en el textarea para mayor claridad
          textareaRef.current.focus();
        }
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.status, gameState.currentPlayerId]);

  // Protección contra salidas accidentales durante una partida en curso
  useEffect(() => {
    const isGameInProgress = () => {
      return gameState.status !== 'WAITING' && 
             gameState.status !== 'FINISHED' && 
             gameState.status !== 'RESULTS';
    };

    // Manejar beforeunload (cerrar/recargar página)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isGameInProgress()) {
        e.preventDefault();
        e.returnValue = t('gameRoom.confirm.beforeUnload');
        return e.returnValue;
      }
    };

    // Manejar navegación hacia atrás (botón de retroceder)
    const handlePopState = (e: PopStateEvent) => {
      if (isGameInProgress()) {
        // Prevenir la navegación inmediatamente
        e.preventDefault();
        window.history.pushState(null, '', window.location.pathname);
        
        // Mostrar el diálogo de confirmación personalizado
        setConfirmDialogConfig({
          title: t('gameRoom.confirm.leaveTitle'),
          message: t('gameRoom.confirm.leaveMessage'),
          onConfirm: () => {
            setShowConfirmDialog(false);
            // Si el usuario confirma, navegar hacia atrás
            pendingNavigationRef.current?.();
            pendingNavigationRef.current = null;
          }
        });
        setShowConfirmDialog(true);
        
        // Guardar la acción de navegación pendiente
        pendingNavigationRef.current = () => {
          window.removeEventListener('popstate', handlePopState);
          window.history.back();
        };
      }
    };

    // Agregar estado inicial para poder detectar el popstate
    window.history.pushState(null, '', window.location.pathname);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [gameState.status]);

  const loadRoomInfo = async () => {
    try {
      const response = await apiClient.get(`/api/rooms/${roomCode}/players`);
      setPlayers(normalizePlayerData(response.data));
    } catch (err) {
      setError(t('gameRoom.errors.loadRoomInfo'));
    }
  };

  const loadGameInfo = async () => {
    try {
      const response = await apiClient.get(`/api/games/${roomCode}/status`, {
        headers: {
          'X-Session-Id': sessionId || ''
        }
      });
      
      logger.log('📊 loadGameInfo - Respuesta del servidor:', {
        categoryId: response.data.categoryId,
        categoryName: response.data.categoryName,
        status: response.data.status
      });
      
      setGameState(prev => {
        // Mapear characterRevealed del backend a character del frontend
        const { characterRevealed, ...otherData } = response.data;
        const newState = { 
          ...prev, 
          ...otherData,
          // Solo actualizar character si characterRevealed está presente en la respuesta
          // Si es null, significa que el jugador es impostor y no debe ver el personaje
          ...(characterRevealed !== undefined && { character: characterRevealed })
        };
        
        logger.log('📊 loadGameInfo - Nuevo estado:', {
          categoryId: newState.categoryId,
          categoryName: newState.categoryName,
          character: newState.character
        });
        
        // Si el juego está en una fase activa (no WAITING o ASSIGNING_ROLES),
        // y el jugador ya tiene información de rol en el estado previo,
        // asegurarse de que hasRoleAssigned se mantenga como true
        if (newState.status !== 'WAITING' && 
            newState.status !== 'ASSIGNING_ROLES' && 
            (prev.isImpostor !== undefined || prev.character)) {
          // El jugador tiene un rol asignado, asegurarse de que la card se muestre
          setHasRoleAssigned(true);
        }
        
        return newState;
      });
      
      // Sincronizar selectedCategoryId con la información del juego
      if (response.data.categoryId) {
        setSelectedCategoryId(response.data.categoryId);
      }
      
      if ((response.data as any).turnDurationSeconds) {
        setTurnTimeRemaining((response.data as any).turnDurationSeconds);
      }

      // Si el juego está activo (no WAITING), intentar cargar información del rol
      if (response.data.status !== 'WAITING') {
        await loadMyRole();
      }
    } catch (err) {
      // El juego puede no haber empezado aún
      // If the backend returns 404 (no active game), ensure UI shows WAITING
      // so reopening the room or a missing game immediately shows the waiting view.
      const e: any = err;
      if (e?.response?.status === 404) {
        // Clear any previously assigned role/character when there's no active game
        setGameState(prev => ({ ...prev, status: 'WAITING', currentRound: 0, isImpostor: undefined, character: undefined }));
        setHasRoleAssigned(false);
        setRoleVisible(false);
        setTurnConfirmed(false);
        setReactionPhase({ active: false, endsAt: 0 });
      }
    }
  };

  const loadMyRole = async () => {
    try {
      // ✅ Verificar que tenemos sessionId antes de hacer la petición
      if (!sessionId) {
        logger.warn('⚠️ Cannot load role: sessionId is null');
        return;
      }
      
      const response = await apiClient.get(`/api/games/${roomCode}/my-role`, {
        headers: {
          'X-Session-Id': sessionId
        }
      });

      const data = response.data;
      
      // Si el servidor indica que el jugador tiene un rol asignado
      if (data.hasRole) {
        logger.log('🎭 loadMyRole - Actualizando estado con:', {
          isImpostor: data.isImpostor,
          character: data.character,
          categoryId: data.categoryId,
          categoryName: data.categoryName
        });
        
        setGameState(prev => ({
          ...prev,
          isImpostor: data.isImpostor,
          character: data.character,
          categoryId: data.categoryId,
          categoryName: data.categoryName
        }));
        setHasRoleAssigned(true);
        logger.log('✅ Rol cargado desde el servidor:', data.isImpostor ? 'IMPOSTOR' : 'JUGADOR', '- Categoría:', data.categoryName);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || '';
      
      // Si el error es por falta de Session ID, puede ser un timing issue
      if (errorMsg.includes('Session ID requerido') && !sessionId) {
        logger.warn('⚠️ loadMyRole failed: sessionId not yet available, will retry');
        // No mostrar error, esto es normal al inicio
        return;
      }
      
      // Si el error es 404 (jugador no encontrado), puede ser timing issue también
      if (err.response?.status === 404) {
        logger.warn('⚠️ loadMyRole failed: player not found (404), may retry later');
        return;
      }
      
      // Otros errores - log pero no mostrar al usuario (no es crítico)
      logger.warn('⚠️ No se pudo cargar información del rol:', errorMsg || err.message);
    }
  };

  const getCurrentPlayerId = () => {
    return players.find(p => p.name === playerName)?.id || 0;
  };

  const toggleRoleVisibility = () => {
    setRoleVisible(prev => !prev);
  };

  const handleRoleCardClick = (e: React.MouseEvent) => {
    // Prevent toggle if clicking on the specific toggle button
    if ((e.target as HTMLElement).closest('.role-toggle-button')) {
      return;
    }
    toggleRoleVisibility();
  };

  const handleToggleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to parent
    toggleRoleVisibility();
  };

  const handleWebSocketMessage = (data: any) => {
    logger.log('🔔 WebSocket message:', data.type);
    
    switch (data.type) {
      case 'PLAYERS_UPDATE':
        logger.log('📢 PLAYERS_UPDATE - updating', data.players?.length, 'players');
        logger.log('📢 PLAYERS_UPDATE - Players data:', data.players);
        logger.log('📢 PLAYERS_UPDATE - Current players before update:', players);
        const normalizedPlayers = normalizePlayerData(data.players);
        logger.log('📢 PLAYERS_UPDATE - Normalized players:', normalizedPlayers);
        setPlayers(normalizedPlayers);
        logger.log('📢 PLAYERS_UPDATE - Update complete');
        break;
      case 'GAME_STARTED':
        logger.log('=== GAME_STARTED MESSAGE ===', {
          categoryId: data.categoryId,
          categoryName: data.categoryName,
          hasCharacter: 'character' in data,
          hasCharacterRevealed: 'characterRevealed' in data,
          character: data.character,
          characterRevealed: data.characterRevealed,
          currentHasRoleAssigned: hasRoleAssigned
        });
        setGameState(prev => ({ 
          ...prev, 
          status: 'ASSIGNING_ROLES',
          categoryId: data.categoryId,
          categoryName: data.categoryName
        }));
        setTurnConfirmed(false);
        setReactionPhase({ active: false, endsAt: 0 });
        
        // Múltiples intentos de obtener el rol si aún no lo tenemos
        logger.log('🔄 Scheduling role fetch attempts...');
        setTimeout(() => {
          logger.log('Attempt 1: Loading role (200ms delay)');
          loadMyRole();
        }, 200);
        setTimeout(() => {
          if (!hasRoleAssigned) {
            logger.log('Attempt 2: Still no role, trying again (800ms delay)');
            loadMyRole();
          }
        }, 800);
        setTimeout(() => {
          if (!hasRoleAssigned) {
            logger.log('Attempt 3: Still no role, final attempt (1500ms delay)');
            loadMyRole();
          }
        }, 1500);
        
        // ✅ ELIMINADO: Los eventos WebSocket nos mantienen sincronizados
        // setTimeout(() => fetchTurnState(), 300);
        break;
      case 'ROUND_START':
        setGameState(prev => ({ 
          ...prev, 
          status: 'ROUND_WRITING',
          currentRound: data.roundNumber 
        }));
        setTimeRemaining(data.durationSeconds);
        setCurrentMessage('');
        setMessages([]);
        setTurnConfirmed(false);
        setReactionPhase({ active: false, endsAt: 0 });
        setProcessingNextPhase(false); // Reset estado de procesamiento
        
        // Asegurarse de que hasRoleAssigned se mantenga si el jugador tiene un rol
        setHasRoleAssigned(prev => {
          // Si ya está asignado, mantenerlo; si no, verificar gameState
          if (prev) return true;
          // Si tenemos información de rol en el estado, marcarlo como asignado
          return gameState.isImpostor !== undefined || !!gameState.character;
        });
        
        // Si aún no tenemos rol asignado, intentar cargarlo
        if (!hasRoleAssigned && (gameState.isImpostor === undefined && !gameState.character)) {
          setTimeout(() => loadMyRole(), 300);
        }
        
        // ✅ ELIMINADO: Los eventos WebSocket nos mantienen sincronizados
        // setTimeout(() => fetchTurnState(), 150);
        break;
      case 'ROUND_TURN_START': {
        const { turnOrder, currentTurnIndex, currentPlayerId, currentPlayerName, secondsRemaining } = data;
        setGameState(prev => ({
          ...prev,
          status: 'ROUND_WRITING',
          currentRound: data.roundNumber,
          turnOrder,
          currentTurnIndex,
          currentPlayerId,
          currentPlayerName,
        }));
        setTurnTimeRemaining(secondsRemaining);
        setCurrentMessage('');
        
        // Asegurarse de que hasRoleAssigned se mantenga si el jugador tiene un rol
        setHasRoleAssigned(prev => {
          if (prev) return true;
          return gameState.isImpostor !== undefined || !!gameState.character;
        });
        
        break;
      }
      case 'TURN_ADVANCE': {
        // ✅ Procesar cambio de turno sin importar el estado actual
        const { currentTurnIndex, currentPlayerId, currentPlayerName, secondsRemaining, roundNumber, gameStatus } = data;
        
        logger.log('🔄 TURN_ADVANCE received:', {
          currentTurnIndex,
          currentPlayerId,
          currentPlayerName,
          secondsRemaining,
          roundNumber,
          gameStatus: gameStatus || 'ROUND_WRITING',
          currentStatus: gameState.status,
          currentRound: gameState.currentRound,
          currentTurnIndexLocal: gameState.currentTurnIndex
        });
        
        // ✅ Validación: Solo procesar si el índice de turno es mayor o igual (evitar actualizaciones obsoletas)
        if (currentTurnIndex !== undefined && gameState.currentTurnIndex !== undefined) {
          if (currentTurnIndex < gameState.currentTurnIndex) {
            logger.log('⚠️ TURN_ADVANCE ignored: received older turn index', currentTurnIndex, 'vs current', gameState.currentTurnIndex);
            break;
          }
        }
        
        // ✅ Actualizar estado completo del turno
        setGameState(prev => ({
          ...prev,
          status: gameStatus || 'ROUND_WRITING', // Usar el estado del servidor o defaultear a ROUND_WRITING
          currentRound: roundNumber || prev.currentRound,
          currentTurnIndex,
          currentPlayerId,
          currentPlayerName,
        }));
        setTurnTimeRemaining(secondsRemaining || gameState.roundDurationSeconds);
        turnTimerStartedRef.current = false; // Reset so auto-confirm doesn't fire prematurely
        setCurrentMessage('');
        setTurnConfirmed(false);
        
        logger.log('✅ Turn state updated, now it\'s', currentPlayerName, '\'s turn');
        break;
      }
      case 'GAME_STATE_UPDATE': {
        // ✅ NUEVO: Actualización completa del estado del juego
        logger.log('🔄 GAME_STATE_UPDATE received:', data);
        
        const update: Partial<GameState> = {};
        
        if (data.gameStatus) update.status = data.gameStatus;
        if (data.currentRound !== undefined) update.currentRound = data.currentRound;
        if (data.categoryId !== undefined) update.categoryId = data.categoryId;
        if (data.categoryName !== undefined) update.categoryName = data.categoryName;
        if (data.turnOrder) update.turnOrder = data.turnOrder;
        if (data.currentTurnIndex !== undefined) {
          // ✅ Validación: Solo actualizar si el índice es más reciente o igual
          if (gameState.currentTurnIndex === undefined || data.currentTurnIndex >= gameState.currentTurnIndex) {
            update.currentTurnIndex = data.currentTurnIndex;
            logger.log('✅ Updating turn index from', gameState.currentTurnIndex, 'to', data.currentTurnIndex);
          } else {
            logger.log('⚠️ Ignoring older turn index', data.currentTurnIndex, 'vs current', gameState.currentTurnIndex);
          }
        }
        if (data.currentPlayerId !== undefined) update.currentPlayerId = data.currentPlayerId;
        if (data.currentPlayerName !== undefined) update.currentPlayerName = data.currentPlayerName;
        
        setGameState(prev => ({
          ...prev,
          ...update
        }));
        
        logger.log('✅ Game state synchronized from server');
        break;
      }
      case 'MESSAGE_SUBMITTED': {
        // ✅ NUEVO: Notificación cuando un jugador envía mensaje
        logger.log('📝 MESSAGE_SUBMITTED:', {
          playerName: data.playerName,
          playerId: data.playerId
        });
        
        // ✅ Mostrar animación del mensaje enviado
        if (data.playerName && data.content) {
          setAnimatingMessage({
            playerName: data.playerName,
            content: data.content,
            visible: true
          });
          
          // Ocultar después de 2.5 segundos (tiempo total de animación)
          setTimeout(() => {
            setAnimatingMessage(null);
          }, 2500);
        }
        break;
      }
      case 'REACTION_PHASE_START': {
        const duration = data.durationSeconds ?? 10;
        const endsAt = Date.now() + duration * 1000;
        setReactionPhase({active: true, endsAt, authorName: data.authorPlayerName, content: data.content});
        // Auto-end safety after duration
        setTimeout(() => setReactionPhase(prev => (Date.now() >= endsAt ? {active: false, endsAt: 0} : prev)), duration * 1000 + 200);
        break;
      }
      case 'REACTION_PHASE_END': {
        setReactionPhase({active: false, endsAt: 0});
        // ✅ ELIMINADO: TURN_ADVANCE ya nos sincroniza el turno
        // setTimeout(() => fetchTurnState(), 300);
        break;
      }
      case 'REACTION': {
        // Spawn reaction animation at random horizontal position
        const x = Math.random() * 80 + 10; // 10% to 90%
        const id = data.id || `${Date.now()}-${Math.random()}`;
        setReactions(prev => [...prev, { id, emoji: data.emoji, x }]);
        // Cleanup after animation duration
        setTimeout(() => {
          setReactions(prev => prev.filter(r => r.id !== id));
        }, 1900);
        break;
      }
      case 'ROUND_TURN_END':
        // Will transition to READING soon by server
        break;
      case 'READING_PHASE':
        setProcessingNextPhase(false); // Reset estado de procesamiento
        setGameState(prev => ({ ...prev, status: 'ROUND_READING' }));
        setMessages(data.messages);
        
        // Asegurarse de que hasRoleAssigned se mantenga si el jugador tiene un rol
        setHasRoleAssigned(prev => {
          if (prev) return true;
          return gameState.isImpostor !== undefined || !!gameState.character;
        });
        
        if (Array.isArray(data.emptyWriters)) {
          setEmptyWriters(data.emptyWriters);
        } else {
          // fallback: derive empties if messages missing or empty content
          const writers = new Set<number>(data.messages.map((m: any) => m.playerId));
          const empties: number[] = players
            .filter(p => !writers.has(p.id))
            .map(p => p.id);
          setEmptyWriters(empties);
        }
        if (typeof data.durationSeconds === 'number') {
          setTimeRemaining(data.durationSeconds);
        } else {
          setTimeRemaining(10);
        }
        break;
      case 'VOTING_PHASE_START':
        setGameState(prev => ({ ...prev, status: 'VOTING' }));
        setTimeRemaining(data.durationSeconds);
        setSelectedVote(null);
        setHasVoted(false); // Reset del estado de votación
        setProcessingVoteResults(false); // Reset del estado de procesamiento
        
        // Asegurarse de que hasRoleAssigned se mantenga si el jugador tiene un rol
        setHasRoleAssigned(prev => {
          if (prev) return true;
          return gameState.isImpostor !== undefined || !!gameState.character;
        });
        
        break;
      case 'SYSTEM_MESSAGE': {
        // ✅ NUEVO: Manejar mensajes del sistema
        logger.log('📢 SYSTEM_MESSAGE received:', data);
        
        const messageType = data.messageType || 'info';
        const message = data.message || t('gameRoom.systemMessage.default');
        
        // Mostrar el mensaje según el tipo
        if (messageType === 'error') {
          setError(message);
          setTimeout(() => setError(''), 5000);
        } else {
          // Para info, warning, success - mostrar en toast o como notificación
          logger.log(`[${messageType.toUpperCase()}] ${message}`);
          // Opcionalmente, podrías agregar un sistema de toast/notificaciones aquí
        }
        break;
      }
      case 'PLAYER_KICKED': {
        // ✅ El jugador ha sido expulsado de la sala
        // ✅ CRÍTICO: Usar sessionId en lugar de playerId para evitar race conditions
        // (PLAYERS_UPDATE puede llegar primero y eliminar al jugador de la lista)
        const targetSessionId = data.sessionId;
        const reason = data.reason || 'KICKED_BY_CREATOR';
        
        logger.log('🚫 PLAYER_KICKED received - Target sessionId:', targetSessionId, 'Current sessionId:', sessionId, 'Reason:', reason);
        
        // Solo actuar si el mensaje es para este jugador (comparar sessionId)
        if (sessionId && targetSessionId && sessionId === targetSessionId) {
          logger.log('🚫 PLAYER_KICKED - You have been kicked from the room');
          
          // Mostrar mensaje apropiado según la razón
          const errorMessage = reason === 'ROOM_CLOSED'
            ? t('gameRoom.errors.kickedRoomClosed')
            : t('gameRoom.errors.kickedByCreator');
          setError(errorMessage);
          
          // Limpiar la sesión
          if (roomCode) {
            clearSession(roomCode);
          }
          
          // Desconectar WebSocket
          if (clientRef.current) {
            clientRef.current.deactivate();
          }
          
          // Redirigir al inicio después de un breve delay para que vea el mensaje
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } else {
          logger.log('🚫 PLAYER_KICKED - Message for another player (sessionId mismatch), ignoring');
        }
        
        break;
      }
      case 'ROOM_CLOSED': {
        // ✅ La sala ha sido cerrada por el creador
        logger.log('🔒 ROOM_CLOSED received for room:', data.roomCode, 'by:', data.creatorName);
        
        // Mostrar mensaje al usuario
        setError(t('gameRoom.errors.roomClosed'));
        
        // Si no somos el creador, salir de la sala
        if (roomInfo?.creatorName !== playerName) {
          // Limpiar la sesión
          if (roomCode) {
            clearSession(roomCode);
          }
          
          // Desconectar WebSocket
          if (clientRef.current) {
            clientRef.current.deactivate();
          }
          
          // Redirigir al inicio después de un breve delay
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
        
        break;
      }
      case 'VOTING_RESULTS':
        setProcessingVoteResults(false); // Desactivar estado de procesamiento
        setGameState(prev => ({ ...prev, status: 'RESULTS' }));
        setVoteResults(data.results);
        
        // Asegurarse de que hasRoleAssigned se mantenga si el jugador tiene un rol
        setHasRoleAssigned(prev => {
          if (prev) return true;
          return gameState.isImpostor !== undefined || !!gameState.character;
        });
        
        // Mostrar mensaje legible sobre eliminación si viene del backend
        if (data.message) {
          setError(''); // limpiar errores previos
          // Guardar en eliminationMessage para mostrarlo en la vista de RESULTADOS
          setEliminationMessage(data.message);
          // Limpiar el mensaje después del tiempo de resultados
          setTimeout(() => setEliminationMessage(null), 9000);
        }
        setTimeRemaining(10); // Tiempo para ver resultados
        break;
      case 'GAME_END':
        setGameState(prev => ({ 
          ...prev, 
          status: 'FINISHED',
          currentRound: data.currentRound || prev.currentRound,
          categoryId: data.categoryId || prev.categoryId,
          categoryName: data.categoryName || prev.categoryName
        }));
        setWinningTeam(data.winningTeam || null);
        setWinningMessage(data.message || null);
        setGameWord(data.gameWord || null);
        setImpostors(data.impostors || []);
        setTurnConfirmed(false);
        setReactionPhase({ active: false, endsAt: 0 });
        // Recargar información de la sala para actualizar estados de jugadores
        loadRoomInfo();
        break;
      case 'CATEGORY_CHANGE':
        // Actualizar selectedCategoryId para todos (no solo para no-creadores)
        setSelectedCategoryId(data.categoryId);
        
        // Actualizar el estado del juego con la información de categoría para todos
        setGameState(prev => ({ 
          ...prev, 
          categoryId: data.categoryId,
          categoryName: data.categoryName 
        }));
        
        break;
      case 'ERROR':
        setError(data.message);
        break;
    }
  };

  const fetchTurnState = async () => {
    try {
      // First check overall game status to avoid forcing WRITING during READING/VOTING
      const statusResp = await apiClient.get(`/api/games/${roomCode}/status`, {
        headers: {
          'X-Session-Id': sessionId || ''
        }
      });
      const statusData = statusResp.data || {};
      const serverStatus = statusData.status;
      if (serverStatus !== 'ROUND_WRITING') return; // only sync turn state in writing phase

      const resp = await apiClient.get(`/api/games/${roomCode}/turn-state`);
      const d = resp.data || {};
      if (d.currentPlayerId != null) {
        setGameState(prev => ({
          ...prev,
          turnOrder: d.turnOrder || prev.turnOrder,
          currentTurnIndex: d.currentTurnIndex ?? prev.currentTurnIndex,
          currentPlayerId: d.currentPlayerId ?? prev.currentPlayerId,
          currentPlayerName: d.currentPlayerName ?? prev.currentPlayerName
        }));
        if (typeof d.secondsRemaining === 'number') {
          setTurnTimeRemaining(d.secondsRemaining);
        }
      }
    } catch (e) {
      // ignore if not available
    }
  };

  const handlePersonalMessage = (data: any) => {
    logger.log('=== PERSONAL MESSAGE RECEIVED ===', data);
    logger.log('Message type:', data.type);
    logger.log('Current hasRoleAssigned:', hasRoleAssigned);
    
    switch (data.type) {
      case 'ROLE_ASSIGNMENT':
        logger.log('🎭 ROLE_ASSIGNMENT received:', {
          isImpostor: data.isImpostor,
          character: data.character,
          categoryId: data.categoryId,
          categoryName: data.categoryName,
          message: data.message
        });
        
        setGameState(prev => ({
          ...prev,
          isImpostor: data.isImpostor,
          character: data.character,
          categoryId: data.categoryId,
          categoryName: data.categoryName
        }));
        setHasRoleAssigned(true);
        logger.log('✅ Role assigned successfully via WebSocket');
        break;
    }
  };

  const startGame = async () => {
    try {
      setStarting(true);
      // Optimistically update local UI so the player that started sees the assigning-roles state
      setGameState(prev => ({ ...prev, status: 'ASSIGNING_ROLES' }));

  await apiClient.post(`/api/games/${roomCode}/start`, { categoryId: selectedCategoryId });

      // Explicitly reload game info so the player that started sees the updated state
      // even if the WebSocket message is delayed or missed.
      await loadGameInfo();

      // Keep the local 'starting' lock for a short grace period to avoid flicker if WS lags.
      setTimeout(() => {
        setStarting(false);
      }, 3000);
    } catch (err: any) {
      setStarting(false);
      // If backend rejects because room is not in waiting, guide the user
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || t('gameRoom.errors.startGame');
      setError(errMsg.includes('not in waiting') || errMsg.toLowerCase().includes('waiting')
        ? t('gameRoom.errors.roomNotWaiting')
        : errMsg);
      // Revert optimistic state if the request failed
      setGameState(prev => ({ ...prev, status: 'WAITING' }));
      // Refresh room info to reflect actual room status
      try { await loadRoomMeta(); } catch {}
    }
  };

  const endCurrentGame = async () => {
    try {
      // Only creator should see/use this, but backend also ensures consistency
      await apiClient.post(`/api/rooms/${roomCode}/reopen`);
      // Reset local UI to waiting state and clear role/character
      setGameState(prev => ({ ...prev, status: 'WAITING', currentRound: 0, isImpostor: undefined, character: undefined }));
      setHasRoleAssigned(false);
      setRoleVisible(false);
      setTurnConfirmed(false);
      setReactionPhase({ active: false, endsAt: 0 });
      // Reload room and game info
      await Promise.all([loadRoomMeta(), loadRoomInfo()]);
      await loadGameInfo();
      setWinningTeam(null);
      setWinningMessage(null);
      setGameWord(null);
      setImpostors([]);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('gameRoom.errors.endGameFailed'));
    }
  };

  // const submitMessage = async () => {
  //   if (!currentMessage.trim()) return;

  //   try {
  //     await apiClient.post(`/api/games/${roomCode}/messages`, {
  //       content: currentMessage,
  //       sessionId
  //     });
  //     setCurrentMessage('');
  //   } catch (err: any) {
  //     setError(err.response?.data?.message || 'Error al enviar mensaje');
  //   }
  // };

  const confirmTurn = async (isAutoConfirm: boolean = false) => {
    try {
      // Actualizar sesión al realizar acción
      if (roomCode) touchSession(roomCode);
      
      // Marcar como confirmado optimísticamente
      setTurnConfirmed(true);
      
      await apiClient.post(`/api/games/${roomCode}/confirm-turn`, {
        content: currentMessage || '',
        sessionId
      });
      
      // Éxito - esperar el mensaje WebSocket TURN_ADVANCE
      logger.log('✅ Turn confirmed successfully');
      
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || t('gameRoom.turn.confirmFailed');
      
      // Errores que indican que el turno ya fue procesado server-side:
      // No resetear turnConfirmed → evitar reenvíos, el WebSocket nos actualizará
      if (errorMsg.includes('ya está avanzando') ||
          errorMsg.includes('No es tu turno') ||
          errorMsg.includes('Not in writing phase') ||
          errorMsg.includes('not in writing phase') ||
          errorMsg.includes('already submitted') ||
          errorMsg.includes('ya envió')) {
        logger.log('⏭️ Turn already handled server-side, waiting for WS update');
        return;
      }
      
      logger.error('❌ Turn confirmation failed:', errorMsg);
      
      // Para auto-confirm (timeout): NUNCA resetear turnConfirmed para evitar bucles de reintento.
      // El backend ya tiene su propio timeout que avanzará el turno.
      if (isAutoConfirm) {
        logger.log('⏭️ Auto-confirm failed, backend timeout will handle it');
        return;
      }
      
      // Solo para clicks manuales del usuario: resetear para permitir reintento
      setTurnConfirmed(false);
      setError(errorMsg);
      
      // Auto-clear error después de 5 segundos
      setTimeout(() => setError(''), 5000);
    }
  };

  // Client-side rate limit for reactions
  const canSendReaction = () => {
    const now = Date.now();
    if (now - lastReactionTsRef.current < 250) return false; // max ~4 per second client-side
    lastReactionTsRef.current = now;
    return true;
  };

  const sendReaction = async (emoji: string) => {
    // ✅ DESACOPLADO: Ya no se requiere reactionPhase.active
    // Los jugadores pueden reaccionar en cualquier momento del juego
    if (!canSendReaction()) return;
    try {
      // Actualizar sesión al realizar acción
      if (roomCode) touchSession(roomCode);
      
      await apiClient.post(`/api/games/${roomCode}/reactions`, { sessionId, emoji });
    } catch (e: any) {
      // Silent fail to avoid spam
    }
  };

  const submitVote = async () => {
    if (!selectedVote || hasVoted) return;

    try {
      // Actualizar sesión al realizar acción
      if (roomCode) touchSession(roomCode);
      
      await apiClient.post(`/api/games/${roomCode}/votes`, {
        votedPlayerId: selectedVote,
        sessionId
      });
      
      // Marcar que ya se votó para deshabilitar el botón
      setHasVoted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || t('gameRoom.errors.voteFailed'));
    }
  };

  const copyRoomLink = async () => {
    if (!roomCode) return;
    const link = `${window.location.origin}${localizePath(`/join?code=${roomCode}`)}`;
    
    try {
      // Intentar usar la API moderna de Clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        setToast({ message: t('gameRoom.clipboard.copied'), type: 'success' });
      } else {
        // Fallback para navegadores que no soportan la API de Clipboard
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setToast({ message: t('gameRoom.clipboard.copied'), type: 'success' });
        } catch (err) {
          logger.error('Error al copiar:', err);
          setToast({ message: t('gameRoom.errors.copyTryAgain'), type: 'error' });
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      logger.error('Error al copiar al portapapeles:', err);
      setToast({ message: t('gameRoom.errors.copyClipboard'), type: 'error' });
      prompt(t('gameRoom.errors.copyPrompt'), link);
    }
  };

  const kickPlayer = async (playerId: number) => {
    try {
      await apiClient.delete(`/api/rooms/${roomCode}/players/${playerId}/kick`, {
        data: { sessionId }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('gameRoom.players.kickFailed'));
    }
  };

  // Función inteligente para salir de la sala
  const handleLeaveRoom = () => {
    const gameInProgress = gameState.status !== 'WAITING' && 
                          gameState.status !== 'FINISHED' && 
                          gameState.status !== 'RESULTS';
    
    if (gameInProgress) {
      // Si hay juego en curso, mostrar confirmación
      setConfirmDialogConfig({
        title: t('gameRoom.confirm.leaveTitle'),
        message: t('gameRoom.confirm.leaveMessage'),
        onConfirm: () => {
          setShowConfirmDialog(false);
          performLeave();
        }
      });
      setShowConfirmDialog(true);
    } else {
      // Si no hay juego activo, salir directamente
      performLeave();
    }
  };

  // Función para cerrar la sala (solo creador)
  const handleCloseRoom = async () => {
    setConfirmDialogConfig({
      title: t('gameRoom.confirm.closeTitle'),
      message: t('gameRoom.confirm.closeMessage'),
      onConfirm: async () => {
        setShowConfirmDialog(false);
        try {
          await apiClient.post(`/api/rooms/${roomCode}/close`, {
            sessionId
          });
          
          setToast({ 
            message: t('gameRoom.toast.roomClosedSuccess'), 
            type: 'success' 
          });
          
          // Esperar un momento para que el usuario vea el mensaje
          setTimeout(() => {
            performLeave();
          }, 1500);
        } catch (err: any) {
          setError(err.response?.data?.error || t('gameRoom.errors.closeRoomFailed'));
        }
      }
    });
    setShowConfirmDialog(true);
  };

  const performLeave = async () => {
    try {
      // CRÍTICO: Llamar al endpoint de salida para eliminar al jugador del backend
      if (roomCode && sessionId) {
        await apiClient.delete(`/api/rooms/${roomCode}/players/${sessionId}`);
        logger.log('✅ Player removed from room backend');
      }
    } catch (err) {
      logger.error('Error removing player from room:', err);
      // Continuar con la limpieza local aunque falle el backend
    }
    
    // Limpiar la sesión local
    if (roomCode) {
      clearSession(roomCode);
    }
    
    // Desconectar WebSocket si existe
    if (clientRef.current) {
      clientRef.current.deactivate();
    }
    
    // Navegar al inicio
    navigate('/');
  };

  const allPlayersReady = () => {
    // const connectedPlayers = players.filter(p => p.isConnected);
    // const readyPlayers = players.filter(p => p.connectionStatus === 'READY');
    
    // Minimum 2 players, all must be ready
    const minPlayersMet = players.length >= 2;
    const allReady = players.length > 0 && players.every(p => p.connectionStatus === 'READY');
    
    const result = minPlayersMet && allReady;
    
    return result;
  };

  const getConnectionStatusText = (connectionStatus: string, isInGame: boolean) => {
    // Durante el juego, ser menos alarmista con el estado de conexión
    if (isInGame && connectionStatus === 'READY') {
      return t('gameRoom.connectionStatus.inGame');
    }
    
    switch (connectionStatus) {
      case 'CONNECTING':
        return t('gameRoom.connectionStatus.connecting');
      case 'READY':
        return t('gameRoom.connectionStatus.ready');
      case 'DISCONNECTED':
        return t('gameRoom.connectionStatus.disconnected');
      default:
        return t('gameRoom.connectionStatus.connecting'); // Default fallback
    }
  };

  const getConnectionStatusColor = (connectionStatus: string, isInGame: boolean) => {
    // Durante el juego, mostrar todos los jugadores "READY" como activos
    if (isInGame && connectionStatus === 'READY') {
      return 'text-blue-600';
    }
    
    switch (connectionStatus) {
      case 'CONNECTING':
        return 'text-yellow-600';
      case 'READY':
        return 'text-green-600';
      case 'DISCONNECTED':
        return 'text-gray-500 dark:text-gray-400'; // Menos alarmante que rojo
      default:
        return 'text-yellow-600'; // Default fallback
    }
  };

  // Normalizar datos de jugadores para asegurar que tengan connectionStatus
  const normalizePlayerData = (playersData: any[]): Player[] => {
    return playersData.map(player => {
      let connectionStatus = player.connectionStatus;
      
      logger.log(`Normalizing player ${player.name}:`, {
        connectionStatus: player.connectionStatus,
        isConnected: player.isConnected
      });
      
      // Si no hay connectionStatus, inferirlo del estado isConnected
      if (!connectionStatus || connectionStatus === null || connectionStatus === undefined) {
        connectionStatus = player.isConnected ? 'CONNECTING' : 'DISCONNECTED';
        logger.log(`  -> Inferred connectionStatus: ${connectionStatus}`);
      }
      
      return {
        ...player,
        connectionStatus: connectionStatus as 'CONNECTING' | 'READY' | 'DISCONNECTED'
      };
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!sessionId || !playerName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-primary-100 to-primary-200 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Card principal */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 space-y-6">
            {/* Icono animado */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Círculo exterior pulsante */}
                <div className="absolute inset-0 bg-primary-400 rounded-full animate-ping opacity-20"></div>
                {/* Círculo medio */}
                <div className="absolute inset-2 bg-primary-300 rounded-full animate-pulse"></div>
                {/* Círculo interior con icono */}
                <div className="relative bg-gradient-to-br from-primary-500 to-primary-600 rounded-full p-8 shadow-lg">
                  <svg 
                    className="w-16 h-16 text-white animate-spin" 
                    style={{ animationDuration: '2s' }}
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    ></circle>
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Texto principal */}
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {t('gameRoom.reconnecting.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {t('gameRoom.reconnecting.restoring')}
              </p>
            </div>

            {/* Barra de progreso animada */}
            <div className="space-y-2">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full animate-pulse"
                  style={{
                    width: '100%',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                ></div>
              </div>
            </div>

            {/* Puntos de carga animados */}
            <div className="flex justify-center space-x-2">
              <div 
                className="w-3 h-3 bg-primary-500 rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div 
                className="w-3 h-3 bg-primary-500 rounded-full animate-bounce"
                style={{ animationDelay: '150ms' }}
              ></div>
              <div 
                className="w-3 h-3 bg-primary-500 rounded-full animate-bounce"
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>

            {/* Mensaje de ayuda */}
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('gameRoom.reconnecting.help')}
              </p>
            </div>
          </div>

          {/* Logo o branding (opcional) */}
          <div className="text-center mt-6">
            <p className="text-primary-700 dark:text-primary-200 font-semibold text-lg">
              {t('gameRoom.reconnecting.brand')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={t('gameRoom.seo.title', { roomCode })}
        description={t('gameRoom.seo.description')}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-950 dark:to-gray-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary-800">
                {t('gameRoom.header.title', { roomCode })}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {t('gameRoom.header.playersConnected', { count: players.length })}
              </p>
              {/* Mostrar avatar elegido por el jugador si existe, sino imagen sample */}
              <div className="mt-3">
                {characterMap[(localStorage.getItem('playerCharacter') || '')] ? (
                  <img src={characterMap[(localStorage.getItem('playerCharacter') || '')]} alt={t('gameRoom.header.yourCharacterAlt')} className="w-24 h-24 mt-1 rounded-md object-cover shadow" loading="lazy" />
                ) : (
                  <img src={sampleImage} alt={t('gameRoom.header.roomArtAlt')} className="w-24 h-24 mt-1 rounded-md object-cover shadow" loading="lazy" />
                )}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <button
                onClick={copyRoomLink}
                className="btn-secondary text-sm w-full sm:w-auto"
              >
                {t('gameRoom.header.copyLink')}
              </button>
              {roomInfo?.creatorName === playerName ? (
                <button
                  onClick={handleCloseRoom}
                  className="btn-secondary text-sm w-full sm:w-auto hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-200 transition-colors"
                >
                  {t('gameRoom.header.closeRoom')}
                </button>
              ) : (
                <button
                  onClick={handleLeaveRoom}
                  className="btn-secondary text-sm w-full sm:w-auto hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-200 transition-colors"
                >
                  {t('gameRoom.header.leave')}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200 px-4 py-3 rounded-lg mb-4">
            {error}
            <button 
              onClick={() => setError('')}
              className="float-right text-red-500 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        )}

        {reconnecting && (
          <div className="fixed inset-0 z-50 min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
              {/* Card principal */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 space-y-6">
                {/* Spinner animado con capas */}
                <div className="relative flex items-center justify-center h-24">
                  {/* Círculo exterior pulsante */}
                  <div className="absolute w-24 h-24 bg-blue-200 rounded-full animate-ping opacity-20"></div>
                  
                  {/* Círculo medio pulsante */}
                  <div className="absolute w-20 h-20 bg-blue-300 rounded-full animate-pulse opacity-30"></div>
                  
                  {/* Spinner giratorio con ícono */}
                  <div className="relative w-16 h-16 flex items-center justify-center">
                    <svg className="animate-spin h-16 w-16 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </div>

                {/* Texto principal */}
                <div className="text-center space-y-3">
                  <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {t('gameRoom.reconnecting.overlayTitle')}
                  </h2>
                  
                  {reconnectAttempts > 0 && (
                    <p className="text-blue-700 dark:text-blue-200 font-medium">
                      {t('gameRoom.reconnecting.attempt', { current: reconnectAttempts + 1, max: MAX_RECONNECT_ATTEMPTS })}
                    </p>
                  )}
                  
                  {/* Barra de progreso animada */}
                  <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-400 via-blue-600 to-blue-400 animate-[shimmer_1.5s_ease-in-out_infinite] bg-[length:200%_100%]"></div>
                  </div>

                  {/* Puntos animados */}
                  <div className="flex justify-center space-x-2 pt-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>

                {/* Mensaje de ayuda */}
                <div className="text-center text-sm text-blue-600 dark:text-blue-200 border-t border-blue-200 dark:border-blue-900 pt-4">
                  <p>{t('gameRoom.reconnecting.server')}</p>
                </div>

                {/* Footer con branding */}
                <div className="text-center text-xs text-blue-400 dark:text-blue-300">
                  <p>{t('gameRoom.reconnecting.overlayFooter')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {reconnectError && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 dark:bg-yellow-950/30 dark:border-yellow-900 dark:text-yellow-200 px-4 py-3 rounded-lg mb-4">
            {reconnectError}
          </div>
        )}

        {/* Indicador de estado de conexión WebSocket */}
        {!reconnecting && (
          <div className={`px-4 py-2 rounded-lg mb-4 flex items-center text-sm ${
            connected 
              ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-950/30 dark:border-green-900 dark:text-green-200' 
              : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="font-medium">
              {connected ? t('gameRoom.websocket.connected') : t('gameRoom.websocket.disconnected')}
            </span>
          </div>
        )}

  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel principal del juego */}
          <div className="lg:col-span-2 space-y-6">
            {gameState.status === 'WAITING' && (
              <div className="game-card text-center">
                {/* Si la sala no está realmente en espera, mostrar aviso y acciones */}
                {roomInfo?.status && roomInfo.status !== 'WAITING' ? (
                  <div>
                    <h2 className="text-xl font-bold mb-4">{t('gameRoom.waiting.inProgressTitle')}</h2>
                    <p className="text-gray-700 dark:text-gray-200 mb-6">
                      {t('gameRoom.waiting.statusPrefix', { status: roomInfo.status })}{' '}
                      {roomInfo?.creatorName === playerName ? t('gameRoom.waiting.creatorHint') : t('gameRoom.waiting.nonCreatorHint')}
                    </p>
                    {roomInfo?.creatorName === playerName ? (
                      <button onClick={endCurrentGame} className="btn-secondary" disabled={!connected}>
                        {t('gameRoom.waiting.endGame')}
                      </button>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{t('gameRoom.waiting.waitingCreatorActions')}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold mb-4">{t('gameRoom.waiting.title')}</h2>
                    <p className="text-gray-700 dark:text-gray-200 mb-6">
                      {t('gameRoom.waiting.minPlayers')}
                    </p>
                    
                    {/* Sección de categoría visible para todos */}
                    {categories.length > 0 && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 dark:bg-blue-950/30 dark:border-blue-900 rounded-lg">
                        <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-100 mb-2">{t('gameRoom.waiting.gameCategoryTitle')}</h3>
                        <div className="text-center">
                          <span className="inline-block bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-100 px-4 py-2 rounded-full text-lg font-medium">
                            {gameState.categoryName || categories.find(c => c.id === selectedCategoryId)?.name || t('gameRoom.waiting.noCategory')}
                          </span>
                        </div>
                        {roomInfo?.creatorName !== playerName && (
                          <p className="text-blue-600 dark:text-blue-200 text-sm mt-2 text-center">
                            {t('gameRoom.waiting.onlyCreatorCanChange')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Selector de categoría para el creador - siempre visible */}
                    {categories.length > 0 && roomInfo?.creatorName === playerName && (
                      <div className="mb-4 text-left">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          {t('gameRoom.waiting.changeCategoryLabel')}
                        </label>
                        <CategorySelector
                          categories={categories}
                          selectedCategoryId={selectedCategoryId}
                          onSelectCategory={async (newCategoryId) => {
                            setSelectedCategoryId(newCategoryId);

                            // Notificar el cambio a otros jugadores
                            if (newCategoryId) {
                              try {
                                await apiClient.post(`/api/games/${roomCode}/category`, {
                                  categoryId: newCategoryId
                                });
                              } catch (err: any) {
                                // Silent fail
                              }
                            }
                          }}
                          disabled={false}
                        />
                      </div>
                    )}

                    {players.length >= 3 && roomInfo?.creatorName === playerName && (
                      <div>
                        <button
                          onClick={startGame}
                          className="btn-primary"
                          disabled={starting || !connected || !roomInfo || roomInfo.status !== 'WAITING' || !allPlayersReady()}
                        >
                          {t('gameRoom.waiting.startGame')}
                        </button>
                        {!allPlayersReady() && players.length >= 3 && (
                          <p className="text-yellow-600 text-sm mt-2">
                            {t('gameRoom.waiting.waitingAllReady')}
                          </p>
                        )}
                      </div>
                    )}
                    {roomInfo?.creatorName !== playerName && (
                      <p className="text-gray-600 dark:text-gray-300 text-sm">
                        {t('gameRoom.waiting.waitingCreatorStart')}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {gameState.status === 'ASSIGNING_ROLES' && (
              <div className="game-card text-center">
                <h2 className="text-xl font-bold mb-4">{t('gameRoom.assigningRoles.title')}</h2>
                <div className="animate-spin mx-auto w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full"></div>
              </div>
            )}

            {hasRoleAssigned && gameState.status !== 'WAITING' && gameState.status !== 'ASSIGNING_ROLES' && (
              <div 
                className={`
                  ${roleVisible 
                    ? (gameState.isImpostor ? "impostor-card" : "player-card")
                    : "game-card"
                  } 
                  cursor-pointer hover:shadow-xl transition-all duration-200 select-none
                  ${roleVisible ? 'hover:opacity-95' : 'hover:scale-[1.02]'}
                `}
                onClick={handleRoleCardClick}
                title={roleVisible ? t('gameRoom.roleCard.tapToHide') : t('gameRoom.roleCard.tapToReveal')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1"></div>
                  <button
                    onClick={handleToggleButtonClick}
                    className="role-toggle-button flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 active:bg-white/50 dark:bg-black/20 dark:hover:bg-black/30 dark:active:bg-black/40 transition-colors duration-200 shadow-sm z-10 relative"
                    title={roleVisible ? t('gameRoom.roleCard.hideRoleInfo') : t('gameRoom.roleCard.showRoleInfo')}
                    aria-label={roleVisible ? t('gameRoom.roleCard.hideRoleInfo') : t('gameRoom.roleCard.showRoleInfo')}
                  >
                    <span className="text-xl">
                      {roleVisible ? "👁️" : "🔒"}
                    </span>
                  </button>
                </div>
                
                {roleVisible ? (
                  <div className="text-center">
                    {gameState.isImpostor === true ? (
                      <>
                        <h2 className="text-2xl font-bold text-impostor-800 dark:text-impostor-200 mb-2">
                          {t('gameRoom.roleCard.impostor.title')}
                        </h2>
                        {gameState.categoryName && (
                          <div className="bg-impostor-100 border border-impostor-300 dark:bg-impostor-900/30 dark:border-impostor-800/40 rounded-lg p-3 mb-3">
                            <p className="text-impostor-800 dark:text-impostor-200 font-bold text-base">{t('gameRoom.roleCard.impostor.remember')}</p>
                            <p className="text-impostor-900 dark:text-impostor-100 font-bold text-lg">{gameState.categoryName.toUpperCase()}</p>
                            <p className="text-impostor-800 dark:text-impostor-200 text-sm mt-1">{t('gameRoom.roleCard.impostor.categoryHint')}</p>
                          </div>
                        )}
                        <p className="text-impostor-800 dark:text-impostor-200 mb-3">
                          {t('gameRoom.roleCard.impostor.instructions')}
                        </p>
                        <div className="text-xs text-impostor-700 dark:text-impostor-200 opacity-80">
                          {t('gameRoom.roleCard.impostor.tapToHide')}
                        </div>
                      </>
                    ) : gameState.isImpostor === false ? (
                      <>
                        <h2 className="text-2xl font-bold text-player-800 dark:text-player-200 mb-2">
                          {t('gameRoom.roleCard.player.title')}
                        </h2>
                        <div className="text-4xl mb-2">{gameState.character || '👤'}</div>
                        {gameState.categoryName && (
                          <div className="bg-player-100 border border-player-300 dark:bg-player-900/30 dark:border-player-800/40 rounded-lg p-3 mb-3">
                            <p className="text-player-800 dark:text-player-200 font-bold text-base">{t('gameRoom.roleCard.player.categoryLead')}</p>
                            <p className="text-player-900 dark:text-player-100 font-bold text-lg">{gameState.categoryName.toUpperCase()}</p>
                            <p className="text-player-800 dark:text-player-200 text-sm mt-1">{t('gameRoom.roleCard.player.remember')}</p>
                          </div>
                        )}
                        <p className="text-player-800 dark:text-player-200 mb-3">
                          {t('gameRoom.roleCard.player.goal')}
                        </p>
                        <div className="text-xs text-player-700 dark:text-player-200 opacity-80">
                          {t('gameRoom.roleCard.player.tapToHide')}
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-200 mb-2">
                          {t('gameRoom.roleCard.generic.title')}
                        </h2>
                        <div className="text-4xl mb-2">{gameState.character || '🎮'}</div>
                        <p className="text-blue-800 dark:text-blue-200 mb-3">
                          {t('gameRoom.roleCard.generic.info')}
                        </p>
                        <div className="text-xs text-blue-700 dark:text-blue-200 opacity-80">
                          {t('gameRoom.roleCard.generic.tapToHide')}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-500 dark:text-gray-200 mb-2">
                      {t('gameRoom.roleCard.hidden.title')}
                    </h2>
                    <p className="text-gray-400 dark:text-gray-400 mb-2">
                      {t('gameRoom.roleCard.hidden.subtitle')}
                    </p>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-300 dark:text-gray-400">
                      <span>👆</span>
                      <span>{t('gameRoom.roleCard.hidden.hint')}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {gameState.status === 'ROUND_WRITING' && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">
                    {t('gameRoom.writing.round', { round: gameState.currentRound })}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {gameState.currentPlayerName && (
                      <span className="text-sm bg-primary-200 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100 px-2 py-1 rounded-full">
                        {t('gameRoom.writing.turnOf', { name: gameState.currentPlayerName })}
                      </span>
                    )}
                    <div className="text-lg font-mono bg-primary-100 text-primary-900 border border-primary-200 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-800/40 px-3 py-1 rounded-lg">
                      ⏱️ {formatTime(turnTimeRemaining || timeRemaining)}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {/* Indicador mejorado cuando ES TU TURNO */}
                  {gameState.currentPlayerId === getCurrentPlayerId() ? (
                    <>
                      <div className="your-turn-indicator text-center p-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl border-4 border-blue-300 shadow-2xl">
                        <div className="text-3xl font-bold text-white mb-2 animate-bounce">
                          {t('gameRoom.writing.yourTurnTitle')}
                        </div>
                        <div className="text-lg text-blue-100 mb-3">
                          {t('gameRoom.writing.yourTurnSubtitle')}
                        </div>
                        <div className="text-sm text-blue-200">
                          {t('gameRoom.writing.yourTurnHint')}
                        </div>
                      </div>
                      
                      <textarea
                        ref={textareaRef}
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        className="w-full p-4 border-4 border-blue-400 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 focus:border-blue-600 shadow-lg text-lg"
                        placeholder={t('gameRoom.writing.placeholder')}
                        maxLength={500}
                        rows={4}
                        disabled={turnConfirmed}
                      />
                      
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={() => confirmTurn(false)}
                          className="btn-primary w-full text-lg py-4 shadow-lg hover:shadow-xl transition-all"
                          disabled={turnConfirmed}
                        >
                          {turnConfirmed ? t('gameRoom.writing.confirmProcessing') : t('gameRoom.writing.confirmSend')}
                        </button>
                      </div>
                    </>
                  ) : (
                    /* Mensaje animado cuando NO es tu turno */
                    <div className="text-center p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-xl border-2 border-gray-300 dark:border-gray-700 shadow-lg">
                      <div className="text-6xl mb-4 animate-pulse">
                        ✍️
                      </div>
                      <div className="text-2xl font-bold text-gray-700 dark:text-gray-100 mb-4">
                        {t('gameRoom.writing.otherIsWriting', { name: gameState.currentPlayerName || t('gameRoom.writing.otherPlayer') })}
                      </div>
                      <div className="typing-dots flex justify-center space-x-2 mb-4">
                        <span className="inline-block w-3 h-3 bg-gray-500 rounded-full"></span>
                        <span className="inline-block w-3 h-3 bg-gray-500 rounded-full"></span>
                        <span className="inline-block w-3 h-3 bg-gray-500 rounded-full"></span>
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">
                        {t('gameRoom.writing.waitTurn')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(gameState.status === 'ROUND_READING' || gameState.status === 'VOTING') && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">
                    {t('gameRoom.reading.title')}
                  </h3>
                  {gameState.status === 'ROUND_READING' && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">{t('gameRoom.reading.votingIn')}</span>
                      <div className="text-lg font-mono bg-blue-100 text-blue-900 px-3 py-1 rounded-lg border-2 border-blue-200 dark:bg-blue-900/30 dark:text-blue-100 dark:border-blue-800/50">
                        ⏱️ {processingNextPhase ? t('gameRoom.reading.preparing') : formatTime(timeRemaining)}
                      </div>
                    </div>
                  )}
                </div>
                {processingNextPhase && gameState.status === 'ROUND_READING' && (
                  <div className="mb-4 p-4 bg-purple-50 border-l-4 border-purple-400 text-purple-800 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-200 rounded flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <div>
                      <p className="font-semibold">{t('gameRoom.reading.preparingVotingTitle')}</p>
                      <p className="text-sm">{t('gameRoom.reading.preparingVotingSubtitle')}</p>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {groupMessagesByPlayer(messages).map((group) => {
                    // ordenar cronológicamente y luego tomar los 3 más recientes
                    const allMsgs = [...group.messages].sort((a,b) => (a.id - b.id));
                    const recent = allMsgs.slice(-3); // últimos 3 cronológicos
                    // mostrar en pantalla con el más reciente arriba
                    const msgs = [...recent].reverse();
                    const player = players.find(p => p.id === group.playerId);
                    const avatarSrc = characterAsset(player?.id === getCurrentPlayerId() ? localStorage.getItem('playerCharacter') || undefined : (player as any)?.character) || sampleImage;

                    const didNotWrite = emptyWriters.includes(group.playerId) || msgs.every(m => !m.content || m.content.trim() === '');
                    return (
                      <div key={group.playerId} className="bg-gradient-to-r from-primary-50 to-primary-100 p-4 rounded-xl border border-primary-200 shadow-sm">
                        <div className="flex items-center space-x-3 mb-3">
                          <img src={avatarSrc as string} alt="avatar" className="w-14 h-14 rounded-lg object-cover border-2 border-primary-200" loading="lazy" />
                          <div>
                            <div className="font-bold text-lg text-primary-800">{player?.name || msgs[0]?.playerName}</div>
                            {didNotWrite && (
                              <div className="text-sm text-red-600">{t('gameRoom.reading.didNotWrite')}</div>
                            )}
                          </div>
                        </div>

                        <hr className="border-t-2 border-primary-200 mb-3" />

                        {/* Contenedor de mensajes: mostrar hasta 3 mensajes visibles (más recientes arriba).
                            Si existen más mensajes en el grupo, el usuario podrá scrollear dentro del bloque. */}
                        <div className="overflow-y-auto" style={{ maxHeight: '10.5rem' }}>
                          <div className="flex flex-col-reverse">
                            {msgs.map((m) => (
                              <div key={m.id} className="bg-white dark:bg-gray-900 p-3 rounded-lg shadow-sm border-l-4 border-primary-400 mb-2 last:mb-0">
                                <div className="text-base font-medium text-gray-800 dark:text-gray-100 leading-relaxed">{m.content}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {gameState.status === 'VOTING' && (
              <>
                {(() => {
                  const currentPlayer = players.find(p => p.name === playerName);
                  const isEliminated = currentPlayer?.status === 'ELIMINATED';
                  
                  if (isEliminated) {
                    // Jugador eliminado - mostrar mensaje informativo
                    return (
                      <div className="card border-2 border-gray-300 dark:border-gray-700 shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-600 dark:text-gray-300">
                              {t('gameRoom.voting.eliminatedTitle')}
                            </h3>
                            <span className="inline-block bg-gray-500 text-white text-xs px-2 py-1 rounded-full mt-1">
                              {t('gameRoom.voting.eliminatedBadge')}
                            </span>
                          </div>
                          <div className="text-lg font-mono bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-lg">
                            ⏱️ {formatTime(timeRemaining)}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 border-l-4 border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded">
                          <p className="font-semibold">{t('gameRoom.voting.eliminatedCantVoteTitle')}</p>
                          <p className="text-sm mt-2">{t('gameRoom.voting.eliminatedCantVoteBody')}</p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Jugador vivo - mostrar interfaz normal de votación
                  return (
                    <div ref={votingSectionRef} className="card border-2 border-red-300 shadow-lg animate-pulse"
                         style={{ animationDuration: '2s', animationIterationCount: '3' }}>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold">
                            {t('gameRoom.voting.activeTitle')}
                          </h3>
                          <span className="inline-block bg-red-500 text-white text-xs px-2 py-1 rounded-full mt-1 animate-bounce">
                            {t('gameRoom.voting.activeBadge')}
                          </span>
                        </div>
                        <div className="text-lg font-mono bg-primary-100 text-primary-900 border border-primary-200 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-800/40 px-3 py-1 rounded-lg">
                          ⏱️ {processingVoteResults ? t('gameRoom.voting.processing') : formatTime(timeRemaining)}
                        </div>
                      </div>
                      {processingVoteResults && (
                        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 dark:border-blue-700 text-blue-800 dark:text-blue-200 rounded flex items-center space-x-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <div>
                            <p className="font-semibold">{t('gameRoom.voting.processingTitle')}</p>
                            <p className="text-sm">{t('gameRoom.voting.processingSubtitle')}</p>
                          </div>
                        </div>
                      )}
                      <div className="space-y-3">
                        {players.filter(p => p.status === 'ALIVE').map((player) => (
                          <label
                            key={player.id}
                            className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              selectedVote === player.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                                : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name="vote"
                              value={player.id}
                              checked={selectedVote === player.id}
                              onChange={(e) => setSelectedVote(parseInt(e.target.value))}
                              className="sr-only"
                            />
                            <div className="font-medium">{player.name}</div>
                          </label>
                        ))}
                      </div>
                      <button
                        onClick={submitVote}
                        disabled={!selectedVote || processingVoteResults || hasVoted}
                        className="btn-primary w-full mt-4 disabled:opacity-50"
                      >
                        {hasVoted
                          ? t('gameRoom.voting.voteSent')
                          : processingVoteResults
                            ? `⏳ ${t('gameRoom.voting.processing')}`
                            : t('gameRoom.voting.confirmVote')}
                      </button>
                    </div>
                  );
                })()}
              </>
            )}

            {gameState.status === 'RESULTS' && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">
                    {t('gameRoom.results.title')}
                  </h3>
                  {timeRemaining > 0 && (
                    <div className="text-lg font-mono bg-green-100 text-green-900 px-3 py-1 rounded-lg border-2 border-green-200 dark:bg-green-900/30 dark:text-green-100 dark:border-green-800/50">
                      ⏱️ {processingNextPhase ? t('gameRoom.results.preparing') : formatTime(timeRemaining)}
                    </div>
                  )}
                </div>
                {processingNextPhase && (
                  <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-400 text-green-800 dark:bg-green-950/30 dark:border-green-700 dark:text-green-200 rounded flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <div>
                      <p className="font-semibold">{t('gameRoom.results.preparingNextTitle')}</p>
                      <p className="text-sm">{t('gameRoom.results.preparingNextSubtitle')}</p>
                    </div>
                  </div>
                )}
                {eliminationMessage && (
                  <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 border-l-4 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded">
                    {eliminationMessage}
                  </div>
                )}
                <div className="space-y-3">
                  {voteResults.map((result) => (
                    <div
                      key={result.playerId}
                      className={`p-3 rounded-lg ${
                        result.eliminated
                          ? 'bg-red-100 border-2 border-red-300 dark:bg-red-950/30 dark:border-red-800'
                          : 'bg-gray-50 dark:bg-gray-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {result.eliminated && '❌ '}
                          {result.playerName}
                        </span>
                        <span className="bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-100 px-2 py-1 rounded-full text-sm">
                          {t('gameRoom.results.votes', { count: result.voteCount })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {gameState.status === 'FINISHED' && (
              <div className="game-card text-center">
                <h2 className="text-2xl font-bold mb-4">{t('gameRoom.finished.title')}</h2>
                
                {gameState.currentRound > 0 && (
                  <p className="text-gray-600 dark:text-gray-300 mb-3">{t('gameRoom.finished.roundsCompleted', { count: gameState.currentRound })}</p>
                )}
                
                {/* Resultado del juego */}
                {winningTeam && (
                  <div className={`border-2 rounded-lg p-4 mb-4 ${
                    winningTeam === 'PLAYERS' ? 'bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800' : 'bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800'
                  }`}>
                    <p className="text-xl font-bold mb-2">
                      {winningTeam === 'PLAYERS' ? t('gameRoom.finished.winnersPlayers') : t('gameRoom.finished.winnersImpostors')}
                    </p>
                    {winningMessage && (
                      <p className="text-gray-700 dark:text-gray-200">{winningMessage}</p>
                    )}
                  </div>
                )}
                
                {/* Palabra del juego */}
                {gameWord && (
                  <div className="bg-purple-50 border-2 border-purple-300 dark:bg-purple-950/30 dark:border-purple-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">{t('gameRoom.finished.wordWas')}</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{gameWord}</p>
                  </div>
                )}
                
                {/* Impostores revelados */}
                {impostors.length > 0 && (
                  <div className="bg-orange-50 border-2 border-orange-300 dark:bg-orange-950/30 dark:border-orange-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {impostors.length === 1 ? t('gameRoom.finished.impostorWas') : t('gameRoom.finished.impostorsWere')}
                    </p>
                    <div className="space-y-2">
                      {impostors.map((impostor) => (
                        <div 
                          key={impostor.id}
                          className="bg-orange-100 border border-orange-200 dark:bg-orange-900/30 dark:border-orange-800/40 rounded-lg px-4 py-2"
                        >
                          <p className="text-lg font-bold text-orange-900 dark:text-orange-100">{impostor.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => navigate('/')}
                    className="btn-primary"
                  >
                    {t('gameRoom.finished.backHome')}
                  </button>

                  <button
                    onClick={async () => {
                      try {
                        await apiClient.post(`/api/rooms/${roomCode}/reopen`);
                        // Immediately update UI to waiting so clients see the waiting view
                        // Also clear role/character so the impostor message doesn't persist
                        setGameState(prev => ({ ...prev, status: 'WAITING', currentRound: 0, isImpostor: undefined, character: undefined }));
                        setHasRoleAssigned(false);
                        setRoleVisible(false);
                        setTurnConfirmed(false);
                        setReactionPhase({ active: false, endsAt: 0 });
                        // Reload players and clear winner info
                        await loadRoomInfo();
                        setWinningTeam(null);
                        setWinningMessage(null);
                        setGameWord(null);
                        setImpostors([]);
                        // Also attempt to fetch game status (will set WAITING on 404)
                        await loadGameInfo();
                      } catch (err: any) {
                        setError(err.response?.data?.message || t('gameRoom.errors.reopenRoomFailed'));
                      }
                    }}
                    className="btn-secondary"
                  >
                    {t('gameRoom.finished.backWaiting')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Panel de jugadores */}
          <div className="card">
            <h3 className="text-lg font-bold mb-4">{t('gameRoom.playersPanel.title')}</h3>
            <div className="space-y-2">
              {players.map((player) => (
                <div
                  key={player.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    player.name === playerName
                      ? 'bg-primary-100 dark:bg-primary-900/20'
                      : player.status === 'ELIMINATED'
                      ? 'bg-red-100 dark:bg-red-950/30'
                      : 'bg-gray-50 dark:bg-gray-900'
                  }`}
                >
                  <div className="flex items-center flex-1">
                        <div className="mr-2">
                          {characterAsset(player.name === playerName ? localStorage.getItem('playerCharacter') || undefined : (player as any).character) ? (
                            <img src={(characterAsset(player.name === playerName ? localStorage.getItem('playerCharacter') || undefined : (player as any).character) as string) || undefined} alt="avatar" className="w-8 h-8 rounded-md object-cover" loading="lazy" />
                          ) : (
                            <div className={`w-3 h-3 rounded-full ${
                              player.connectionStatus === 'READY' ? 'bg-green-500' : 
                              player.connectionStatus === 'CONNECTING' ? 'bg-yellow-500' : 
                              'bg-gray-400'
                            }`}></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className={player.status === 'ELIMINATED' ? 'line-through text-gray-500 dark:text-gray-400' : ''}>
                              {player.name}
                            </span>
                            {player.name === playerName && (
                              <span className="ml-2 text-xs bg-primary-600 text-white px-2 py-1 rounded-full">
                                {t('gameRoom.playersPanel.you')}
                              </span>
                            )}
                          </div>
                          {/* Estado de conexión */}
                          <div className={`text-xs ${getConnectionStatusColor(player.connectionStatus, gameState.status !== 'WAITING')}`}>
                            {getConnectionStatusText(player.connectionStatus, gameState.status !== 'WAITING')}
                          </div>
                        </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {player.status === 'ELIMINATED' && (
                      <span className="text-red-500 text-sm">❌</span>
                    )}
                    {/* Botón de expulsar solo para el creador */}
                    {roomInfo?.creatorName === playerName && 
                     player.name !== playerName && 
                     gameState.status === 'WAITING' && (
                      <button
                        onClick={() => kickPlayer(player.id)}
                        className="text-red-500 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200 text-sm p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30"
                        title={t('gameRoom.playersPanel.kick', { name: player.name })}
                      >
                        🚫
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ✅ Reaction UI - Siempre visible durante el juego activo (desacoplado de ventanas) */}
        {gameState.status !== 'WAITING' && gameState.status !== 'FINISHED' && (
          <>
            {/* Botones de reacciones siempre disponibles */}
            <div ref={reactionButtonsRef} className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur border border-primary-200 dark:border-gray-700 shadow-lg rounded-full px-4 py-2 flex gap-2">
              {['🤔','😂','😢','👍','🔥','💀','❤️','🎉'].map((emo) => (
                <button
                  key={emo}
                  onClick={() => sendReaction(emo)}
                  className="text-2xl hover:scale-110 transition-transform active:scale-95"
                  title={t('gameRoom.reactions.send')}
                >{emo}</button>
              ))}
            </div>
            {/* Animations container */}
            <div className="reaction-container">
              {reactions.map(r => (
                <div key={r.id} className="reaction-item" style={{ left: `${r.x}%` }}>{r.emoji}</div>
              ))}
            </div>
          </>
        )}
        
        {/* ✅ Message Animation - Aparece cuando un jugador envía mensaje */}
        {animatingMessage && (
          <div className="message-animation-overlay">
            <div className="message-animation-card">
              <div className="message-animation-player">
                📝 {animatingMessage.playerName}
              </div>
              <div className="message-animation-content">
                {animatingMessage.content}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dialog de confirmación personalizado */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        confirmText={t('common.leave')}
        cancelText={t('common.stay')}
        danger={true}
        onConfirm={confirmDialogConfig.onConfirm}
        onCancel={() => {
          setShowConfirmDialog(false);
          pendingNavigationRef.current = null;
        }}
      />

      {/* Toast de notificaciones */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
    </>
  );
};

export default GameRoom;

